import { THREAD_ENTITY_TYPES } from '@peles/shared';

/**
 * Where a conversation's Link chip goes when it is clicked
 * (COMMUNICATIONS-DESIGN H1).
 *
 * ⚠️ **The route half of the registry, and it lives here because a route is a
 * path in a router the shared package cannot see.** The labels are in
 * `@peles/shared` (`thread-entity.ts`) so the backend can put them on a row;
 * only the frontend knows that an event is at `/events/:id`.
 *
 * ⚠️ **Two lists that can drift is the whole risk**, so `entityRoutes.test.ts`
 * asserts the key sets match exactly. A record type declared with no route
 * would render a chip that links nowhere; a route for a type nothing declares
 * would be dead code nobody notices.
 *
 * ⚠️ **Not every record has a detail page, and that is honest rather than a
 * gap.** An expense line, a guest and a planning item are edited in modals on a
 * list, so the most specific address they have is that list — filtered where
 * the list supports it. Returning the list is right; inventing
 * `/expenses/:id` for a page that does not exist would be a 404 dressed as a
 * link.
 */
type RouteFor = (entityId: string) => string;

const ROUTES: Record<string, RouteFor> = {
  Event: (id) => `/events/${id}`,
  Vendor: (id) => `/vendors/${id}`,
  Venue: (id) => `/venues/${id}`,
  User: () => '/users',
  Inquiry: () => '/inquiries',
  Questionnaire: () => '/questionnaires',
  Checklist: () => '/checklists',
  Timeline: () => '/timelines',
  // No detail page: the list is the most specific address these have.
  ExpenseItem: (id) => `/expenses?expenseItemId=${encodeURIComponent(id)}`,
  Guest: () => '/guests',
  PlanningItem: () => '/expenses',
  Document: () => '/files',
};

/** The path a thread's record is at, or null when there is nowhere to go. */
export function entityRoute(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
): string | null {
  if (!entityType || !entityId) return null;
  const route = ROUTES[entityType];
  return route ? route(entityId) : null;
}

/** The declared keys, so a test can hold them against the shared list. */
export function routedEntityTypes(): string[] {
  return Object.keys(ROUTES);
}

/** The shared keys, re-exported so the parity test reads as one comparison. */
export function labelledEntityTypes(): string[] {
  return THREAD_ENTITY_TYPES.map((entity) => entity.key);
}
